import api from './api';

export interface Order {
  id: number;
  customerId: number;
  partnerId: number;
  truckId: number;
  serviceId: number;
  pickupAddressId: number;
  deliveryAddressId: number;
  status: string;
  totalAmount: number;
  scheduledDate: string;
  actualPickupDate?: string;
  actualDeliveryDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Relations
  customer?: any;
  partner?: any;
  truck?: any;
  service?: any;
  pickupAddress?: any;
  deliveryAddress?: any;
}

export interface CreateOrderData {
  customerId: number;
  partnerId: number;
  truckId: number;
  serviceId: number;
  pickupAddressId: number;
  deliveryAddressId: number;
  scheduledDate: string;
  totalAmount: number;
  notes?: string;
}

export interface UpdateOrderData {
  status?: string;
  actualPickupDate?: string;
  actualDeliveryDate?: string;
  notes?: string;
  totalAmount?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

class OrdersService {
  async getAllOrders(page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get('/orders', {
      params: { page, limit }
    });
    return response.data;
  }

  async getOrderById(id: number): Promise<Order> {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  }

  async createOrder(orderData: CreateOrderData): Promise<Order> {
    const response = await api.post('/orders', orderData);
    return response.data;
  }

  async updateOrder(id: number, orderData: UpdateOrderData): Promise<Order> {
    const response = await api.patch(`/orders/${id}`, orderData);
    return response.data;
  }

  async deleteOrder(id: number): Promise<void> {
    await api.delete(`/orders/${id}`);
  }

  async getOrdersByCustomer(customerId: number, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get(`/orders/customer/${customerId}`, {
      params: { page, limit }
    });
    return response.data;
  }

  async getOrdersByPartner(partnerId: number, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get(`/orders/partner/${partnerId}`, {
      params: { page, limit }
    });
    return response.data;
  }

  async getOrdersByStatus(status: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get('/orders', {
      params: { 
        status,
        page, 
        limit 
      }
    });
    return response.data;
  }

  async updateOrderStatus(id: number, status: string): Promise<Order> {
    return this.updateOrder(id, { status });
  }

  async searchOrders(query: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get('/orders', {
      params: { 
        search: query,
        page, 
        limit 
      }
    });
    return response.data;
  }

  async getOrdersByDateRange(startDate: string, endDate: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Order>> {
    const response = await api.get('/orders', {
      params: { 
        startDate,
        endDate,
        page, 
        limit 
      }
    });
    return response.data;
  }
}

const ordersService = new OrdersService();

export default ordersService;
